import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { formatNOK, formatDate } from "@/lib/format";
import { SrIcon } from "@/components/ui/SrIcon";
import { UpsellAttachmentBadge } from "../UpsellAttachmentBadge";
import type { UpsellWithLines } from "@/hooks/use-upsells";

/** Ferdig utregnet rad-view-model — all beregning (fastpris-totals,
 *  T&M-fakturert-hittil) gjøres i MersalgTab, denne komponenten er ren
 *  presentasjon (tabellmarkup) for å holde MersalgTab.tsx under fillengde-
 *  grensen (endringssjekk-skillen). */
export interface MersalgRowData {
  upsell: UpsellWithLines;
  isTM: boolean;
  margin: number;
  total: number;
  /** VIB-994: Σ invoices.amount_excl_vat der invoices.upsell_id = upsell.id. */
  tmInvoicedTotal: number;
}

interface Props {
  rows: MersalgRowData[];
  variant: "draft" | "approved";
  canCrud: boolean;
  canApprove: boolean;
  activeUserId: string | undefined;
  isApproving: boolean;
  onApprove: (upsell: UpsellWithLines) => void;
  onEdit: (upsell: UpsellWithLines) => void;
  onDelete: (upsell: UpsellWithLines) => void;
}

export function MersalgTable({
  rows,
  variant,
  canCrud,
  canApprove,
  activeUserId,
  isApproving,
  onApprove,
  onEdit,
  onDelete,
}: Props) {
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b">
          <th className="text-left py-2 px-4 font-medium text-muted-foreground text-xs uppercase tracking-wider">
            Mersalg
          </th>
          <th className="text-center py-2 px-4 font-medium text-muted-foreground text-xs uppercase tracking-wider w-24">
            Linjer
          </th>
          <th className="text-right py-2 px-4 font-medium text-muted-foreground text-xs uppercase tracking-wider w-28">
            Margin
          </th>
          <th className="text-right py-2 px-4 font-medium text-muted-foreground text-xs uppercase tracking-wider w-32">
            Sum / fakturert
          </th>
          <th className="text-right py-2 px-4 font-medium text-muted-foreground text-xs uppercase tracking-wider w-24">
            Dato
          </th>
          <th className="w-32"></th>
        </tr>
      </thead>
      <tbody>
        {rows.map(({ upsell: u, isTM, margin, total, tmInvoicedTotal }) => (
          <tr
            key={u.id}
            className="border-b border-border/50 last:border-0 hover:bg-muted/30 group cursor-pointer"
            onClick={() => onEdit(u)}
          >
            <td className="py-2.5 px-4">
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="font-medium">{u.description}</span>
                {variant === "draft" && (
                  <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 uppercase tracking-wider">
                    Utkast
                  </span>
                )}
                {/* VIB-994: skiller T&M-mersalg (medgått tid + materiell) fra fastpris. */}
                {isTM && (
                  <span
                    className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-800 uppercase tracking-wider"
                    title="Faktureres løpende på medgått tid og materiell"
                  >
                    <SrIcon name="clock" className="w-3 h-3" />
                    T&amp;M
                  </span>
                )}
                {u.source_voucher_id && (
                  <span
                    className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-primary/10 text-primary uppercase tracking-wider"
                    title="Opprettet fra bilag"
                  >
                    <SrIcon name="receipt" className="w-3 h-3" />
                    Fra bilag
                  </span>
                )}
                {u.attachment_path && (
                  <UpsellAttachmentBadge path={u.attachment_path} filename={u.attachment_filename} />
                )}
              </div>
              {u.category && <span className="text-xs text-muted-foreground">{u.category}</span>}
            </td>
            <td className="py-2.5 px-4 text-center tabular-nums">{u.lines.length}</td>
            {isTM ? (
              <>
                {/* VIB-994: ingen fastsum-margin for T&M — fakturagrunnlaget er
                    loggede timer/bilag, ikke linjenes estimat-beløp. */}
                <td className="py-2.5 px-4 text-right tabular-nums text-muted-foreground">—</td>
                <td
                  className="py-2.5 px-4 text-right tabular-nums font-medium"
                  title="Fakturert hittil (T&M, fra invoices.upsell_id — kan ha flere fakturaer)"
                >
                  {formatNOK(tmInvoicedTotal)}
                </td>
              </>
            ) : (
              <>
                <td
                  className={cn(
                    "py-2.5 px-4 text-right tabular-nums font-medium",
                    margin >= 0 ? "text-success" : "text-destructive",
                  )}
                >
                  {formatNOK(margin)}
                </td>
                <td className="py-2.5 px-4 text-right tabular-nums font-medium">{formatNOK(total)}</td>
              </>
            )}
            <td className="py-2.5 px-4 text-right text-muted-foreground text-xs">
              {formatDate(u.created_at)}
            </td>
            <td className="py-2.5 px-4 text-right">
              {canCrud && (
                <div className="flex items-center gap-1 justify-end opacity-0 group-hover:opacity-100 focus-within:opacity-100 group-focus-within:opacity-100 transition-opacity">
                  {variant === "draft" && canApprove && activeUserId && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 text-xs"
                      onClick={(e) => {
                        e.stopPropagation();
                        onApprove(u);
                      }}
                      disabled={isApproving}
                      aria-label="Godkjenn"
                    >
                      <SrIcon name="check" className="w-3 h-3 mr-1" />
                      Godkjenn
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-muted-foreground hover:text-primary"
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit(u);
                    }}
                    aria-label="Rediger"
                  >
                    <SrIcon name="pencil" className="w-3.5 h-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-muted-foreground hover:text-destructive"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(u);
                    }}
                    aria-label="Slett"
                  >
                    <SrIcon name="trash2" className="w-3.5 h-3.5" />
                  </Button>
                </div>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
